import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, NavLink, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faUsers,
  faBoxOpen,
  faSyncAlt,
  faKey,
  faUserTag,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";

/** Reusable sidebar item with hover tooltip when collapsed */
const SidebarItem = ({ to, icon, label, isActive, collapsed }) => {
  return (
    <li className="relative">
      <NavLink
        to={to}
        className={[
          "group relative flex items-center p-2 space-x-2 rounded-md hover:bg-gray-100 transition-colors",
          collapsed ? "justify-center" : "",
          isActive ? "bg-gray-200 text-blue-700 font-semibold" : "",
        ].join(" ")}
        aria-label={label}
        // title as a native fallback tooltip
        title={collapsed ? label : undefined}
      >
        <FontAwesomeIcon icon={icon} className="text-gray-700" />
        {/* Hide text when collapsed */}
        <span className={collapsed ? "hidden lg:inline d-hidden" : ""}>
          {label}
        </span>

        {/* Custom tooltip (shown only when collapsed) */}
        {collapsed && (
          <span
            className={[
              // hidden by default
              "pointer-events-none absolute left-full top-1/2 -translate-y-1/2",
              "ml-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0",
              "shadow-lg transition-opacity duration-150",
              // show on hover
              "group-hover:opacity-100",
            ].join(" ")}
          >
            {label}
          </span>
        )}
      </NavLink>
    </li>
  );
};

const getInitials = (name = "") => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const Layout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [isSidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("isSidebarOpen");
    return saved ? JSON.parse(saved) : false;
  });
  const [isSettingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem("isSidebarOpen", JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 300);
    const user = window.localStorage.getItem("user_info");
    if (!user) {
      logout();
    }
    console.log("user", user);
    setUserInfo(JSON.parse(user));
    return () => clearTimeout(timer);
  }, []);

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_info");
    navigate("/");
  };

  // keep section active on list + add/edit/view
  const isActiveGroup = (...prefixes) =>
    prefixes.some((p) => pathname.startsWith(p));

  if (loading) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center text-white bg-black/50"
        style={{ backdropFilter: "blur(14px)" }}
      >
        Loading.....
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      {/* Sidebar */}
      <aside
        className={[
          "fixed inset-y-0 z-10 flex flex-col bg-white shadow-lg border-r transition-transform transform",
          "w-64",
          !isSidebarOpen ? "-translate-x-full lg:translate-x-0 lg:w-20" : "",
        ].join(" ")}
      >
        <div
          className={[
            "flex items-center justify-between p-2",
            !isSidebarOpen ? "lg:justify-center" : "",
          ].join(" ")}
        >
          <span className="p-2 text-xl font-semibold leading-8 tracking-wider uppercase">
            K
            <span className={!isSidebarOpen ? "hidden lg:inline d-hidden" : ""}>
              ode Fast
            </span>
          </span>
          <button
            onClick={() => setSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-md lg:hidden btn-no-overlay"
          >
            <svg
              className="w-6 h-6 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <nav className="flex-1 overflow-hidden hover:overflow-y-auto">
          <ul className="p-2 space-y-1">
            {/* Dashboard */}
            <SidebarItem
              to="/"
              icon={faUser}
              label="Dashboard"
              isActive={pathname === "/" || isActiveGroup("/dashboard")}
              collapsed={!isSidebarOpen}
            />

            {/* Clients */}
            <SidebarItem
              to="/clients"
              icon={faUserTag}
              label="Clients"
              isActive={isActiveGroup(
                "/clients",
                "/clientsAdd",
                "/clientsEdit",
                "/clientsView"
              )}
              collapsed={!isSidebarOpen}
            />

            {/* Users */}
            <SidebarItem
              to="/users"
              icon={faUsers}
              label="Users"
              isActive={isActiveGroup("/users")}
              collapsed={!isSidebarOpen}
            />

            {/* Customers */}
            <SidebarItem
              to="/customers"
              icon={faUser}
              label="Customers"
              isActive={isActiveGroup(
                "/customers",
                "/customersAdd",
                "/customersEdit",
                "/customersView"
              )}
              collapsed={!isSidebarOpen}
            />

            {/* Packages */}
            <SidebarItem
              to="/packages"
              icon={faBoxOpen}
              label="Packages"
              isActive={isActiveGroup(
                "/packages",
                "/packagesAdd",
                "/packagesEdit",
                "/packagesView"
              )}
              collapsed={!isSidebarOpen}
            />

            {/* Access Tokens */}
            <SidebarItem
              to="/accesstokens"
              icon={faSyncAlt}
              label="Access Tokens"
              isActive={isActiveGroup("/accesstokens")}
              collapsed={!isSidebarOpen}
            />

            {/* Refresh Tokens */}
            <SidebarItem
              to="/refreshtokens"
              icon={faKey}
              label="Refresh Tokens"
              isActive={isActiveGroup("/refreshtokens")}
              collapsed={!isSidebarOpen}
            />
          </ul>
        </nav>

        <div className="p-2 border-t">
          <button
            className="w-full flex items-center justify-center space-x-1 px-4 py-2 text-sm font-medium tracking-wider uppercase bg-gray-100 border rounded-md btn-no-overlay"
            onClick={logout}
            title={!isSidebarOpen ? "Logout" : undefined}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span className={!isSidebarOpen ? "hidden lg:inline d-hidden" : ""}>
              Logout
            </span>

            {/* Logout tooltip when collapsed */}
            {!isSidebarOpen && (
              <span className="pointer-events-none absolute left-20 top-auto ml-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100" />
            )}
          </button>
        </div>
      </aside>

      {/* Main Layout */}
      <div
        className={[
          "flex flex-col flex-1 h-full overflow-hidden transition-transform transform",
          !isSidebarOpen ? "aside-menu-open" : "aside-menu-close",
        ].join(" ")}
      >
        <header className="flex-shrink-0 border-b p-2 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <span className="text-xl font-semibold lg:hidden">Kodefast</span>
            <button
              onClick={() => setSidebarOpen(!isSidebarOpen)}
              className="p-1 rounded-md btn-no-overlay"
              title={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              <svg
                className={`w-4 h-4 text-gray-600 transform transition-transform ${
                  isSidebarOpen ? "-rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSettingsPanelOpen(true)}
              className="px-3 py-1 text-sm bg-gray-600 text-white rounded btn-no-overlay"
              title="Open settings"
            >
              <FontAwesomeIcon icon={faUser} className="text-red-500" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4">
          <Outlet />
        </main>

        <footer className="flex items-center justify-between p-4 border-t">
          <div>Kodefast &copy; 2025</div>
        </footer>
      </div>

      <AnimatePresence>
        {isSettingsPanelOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 bg-black/40 z-40"
              onClick={() => setSettingsPanelOpen(false)}
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 180, damping: 22 }}
              className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-50 p-6 flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {userInfo?.name} User Info
                </h2>
                <button
                  onClick={() => setSettingsPanelOpen(false)}
                  className="text-slate-500 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>
              <div className="flex items-center gap-4 w-full">
                {/* Left: Initial + clock stacked */}
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center text-2xl font-bold text-rose-700 shadow-inner">
                    {getInitials(userInfo?.name)}
                  </div>
                </div>

                {/* Right: Name & user type */}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">{userInfo?.name}</h3>
                  <p className="text-sm text-slate-500">{userInfo?.userType}</p>
                </div>
              </div>

              {/* Divider */}
              <div className="my-6 border-t" />
              {/* Details */}
              <div className=" space-y-2">
                <div className="bg-slate-50 p-4 rounded-xl border shadow-sm">
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium">{userInfo?.email}</p>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl border shadow-sm">
                  <p className="text-xs text-slate-500">Phone</p>
                  <p className="font-medium">+{userInfo?.phoneNumber}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-auto pt-3 flex gap-3">
                <button className="flex-1 py-2 px-4 rounded-lg bg-rose-600 text-white font-medium shadow hover:brightness-95">
                  Edit User
                </button>
                <button
                  className="flex-1 py-2 px-4 rounded-lg bg-white border font-medium hover:bg-slate-50"
                  onClick={() => setSettingsPanelOpen(false)}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;
